/**
 * OrgSwitcher — switch between personal and org accounts.
 */
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { useAppStore } from '@/store/appStore';
import type { Id } from '../../convex/_generated/dataModel';
import { ChevronDown, Building2, User, Plus, Users } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';

export function OrgSwitcher() {
  const orgs = useQuery(api.organizations.listForUser, {});
  const createOrg = useMutation(api.organizations.create);
  const setSeatLimit = useMutation(api.organizations.setSeatLimit);
  const { currentAccountId, setCurrentAccountId } = useAppStore();
  const setCurrentTeamId = useAppStore((s) => s.setCurrentTeamId);
  const [open, setOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [editingSeatsFor, setEditingSeatsFor] = useState<Id<'organizations'> | null>(null);
  const [seatLimitInput, setSeatLimitInput] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fn = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('click', fn);
    return () => document.removeEventListener('click', fn);
  }, []);

  const displayLabel =
    currentAccountId && orgs?.find((o) => o._id === currentAccountId)
      ? orgs.find((o) => o._id === currentAccountId)!.name
      : 'Personal';

  const handleCreateOrg = async () => {
    const n = createName.trim() || 'My organization';
    try {
      await createOrg({ name: n });
      setCreateName('');
      setOpen(false);
    } catch (err) {
      console.error('Create org failed:', err);
    }
  };

  const handleSetSeatLimit = async (orgId: Id<'organizations'>) => {
    const n = parseInt(seatLimitInput, 10);
    if (isNaN(n) || n < 1) return;
    try {
      await setSeatLimit({ id: orgId, seatLimit: n });
      setEditingSeatsFor(null);
      setSeatLimitInput('');
    } catch (err) {
      console.error('Set seat limit failed:', err);
    }
  };

  if (!orgs) return null;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm text-muted-foreground hover:bg-muted/50"
      >
        {currentAccountId ? (
          <Building2 className="h-4 w-4" />
        ) : (
          <User className="h-4 w-4" />
        )}
        <span className="truncate max-w-[120px]">{displayLabel}</span>
        <ChevronDown className="h-4 w-4" />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 w-52 rounded-lg border border-border bg-card shadow-lg py-1 z-50">
          {orgs.length === 0 ? (
            <div className="px-3 py-2 space-y-2">
              <p className="text-xs text-muted-foreground">No organizations yet</p>
              <input
                type="text"
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                placeholder="Org name"
                className="w-full rounded border border-input bg-background px-2 py-1.5 text-sm"
              />
              <button
                onClick={handleCreateOrg}
                className="flex items-center gap-2 w-full rounded bg-primary px-2 py-1.5 text-sm text-primary-foreground hover:bg-primary/90"
              >
                <Plus className="h-4 w-4" />
                Create organization
              </button>
            </div>
          ) : (
            <>
          <button
            onClick={() => {
              setCurrentAccountId(null);
              setCurrentTeamId(null);
              setOpen(false);
            }}
            className="flex items-center gap-2 w-full px-3 py-2 text-left text-sm hover:bg-muted/50"
          >
            <User className="h-4 w-4" />
            Personal
          </button>
          {orgs.map((o) => (
            <div key={o._id} className="flex flex-col gap-0.5">
              <button
onClick={() => {
                setCurrentAccountId(o._id);
                setCurrentTeamId(null);
                setOpen(false);
                setEditingSeatsFor(null);
              }}
                className="flex items-center gap-2 w-full px-3 py-2 text-left text-sm hover:bg-muted/50"
              >
                <Building2 className="h-4 w-4 flex-shrink-0" />
                <span className="truncate flex-1">{o.name}</span>
                {'memberCount' in o && (o.seatLimit != null || (o as { memberCount?: number }).memberCount != null) && (
                  <span className="text-xs text-muted-foreground flex-shrink-0">
                    <Users className="h-3 w-3 inline mr-0.5" />
                    {(o as { memberCount: number }).memberCount}
                    {o.seatLimit != null ? `/${o.seatLimit}` : ''}
                  </span>
                )}
              </button>
              {editingSeatsFor === o._id ? (
                <div className="px-3 pb-2 flex items-center gap-2">
                  <input
                    type="number"
                    min={1}
                    value={seatLimitInput}
                    onChange={(e) => setSeatLimitInput(e.target.value)}
                    placeholder="Seat limit"
                    className="w-20 rounded border border-input bg-background px-2 py-1 text-sm"
                  />
                  <button
                    onClick={() => handleSetSeatLimit(o._id)}
                    className="rounded bg-primary px-2 py-1 text-xs text-primary-foreground"
                  >
                    Set
                  </button>
                  <button
                    onClick={() => { setEditingSeatsFor(null); setSeatLimitInput(''); }}
                    className="text-xs text-muted-foreground"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  onClick={(e) => { e.stopPropagation(); setEditingSeatsFor(o._id); setSeatLimitInput(o.seatLimit?.toString() ?? ''); }}
                  className="flex items-center gap-1 w-full px-3 py-1 text-left text-xs text-muted-foreground hover:bg-muted/30"
                >
                  {o.seatLimit != null ? 'Change seat limit' : 'Set seat limit'}
                </button>
              )}
            </div>
          ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
